/**
 * Parent service landing page template.
 *
 * URL pattern:  /:service   (e.g. /car-transport, /furniture-delivery, /house-removals)
 *
 * Word-count target: 1800-2500 (intro + deepDive + sections + FAQs typically deliver ~2000).
 */
import { useMemo } from "react";
import { SeoNav, SeoFooter } from "./shared/Chrome";
import { SeoHelmet } from "./shared/SeoHelmet";
import {
  Breadcrumbs,
  SeoHero,
  IntroSection,
  HowItWorks,
  WhyChoose,
  AreasCovered,
  FaqSection,
  RelatedServices,
  RelatedLocations,
  CtaSection,
} from "./shared/Sections";
import {
  buildBreadcrumbSchema,
  buildServiceSchema,
  buildFaqSchema,
  canonicalFor,
} from "../lib/schema";
import { CITY_LIST } from "../data/cities";
import { getServicesBySilo, SILOS } from "../data/services";

export default function ServiceLandingTemplate({ service }) {
  const pageUrl = `/${service.slug}`;
  const canonical = canonicalFor(pageUrl);

  const siblings = useMemo(
    () => getServicesBySilo(service.silo).filter((s) => s.slug !== service.slug).slice(0, 4),
    [service.silo, service.slug],
  );

  // Top 12 cities for related-locations grid (gives Phase-1 cities visibility on parent pages).
  const featuredCities = useMemo(() => CITY_LIST.slice(0, 12), []);

  const ukRegions = useMemo(
    () => [
      "London", "Greater Manchester", "West Midlands", "West Yorkshire", "Merseyside", "South Yorkshire",
      "South West", "Scotland", "Wales", "Northern Ireland", "East Midlands", "North East",
    ],
    [],
  );

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: SILOS[service.silo].label, url: "#" },
    { name: service.name, url: pageUrl },
  ];

  const schemas = [
    buildBreadcrumbSchema(breadcrumbs),
    buildServiceSchema({ service, city: null, pageUrl }),
    buildFaqSchema(service.faqs),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`seo-service-page-${service.slug}`}>
      <SeoHelmet
        title={service.titleParent}
        description={service.metaDescParent}
        canonical={canonical}
        schemas={schemas}
      />
      <SeoNav />
      <SeoHero
        eyebrow={`UK-wide ${SILOS[service.silo].label}`}
        h1={service.h1Parent}
        lead={service.intro}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <IntroSection
        heading={`Professional ${service.name} Across the UK`}
        body={service.deepDive}
      />
      <HowItWorks steps={service.howItWorks} />
      <WhyChoose reasons={service.whyChoose} />
      <AreasCovered
        heading={`${service.name} Coverage Across the UK`}
        body={`We provide ${service.name.toLowerCase()} in every major UK metro area and across the surrounding regions. From dense city-centre collections to remote rural delivery points, our driver network covers the whole of mainland Britain plus Northern Ireland via ferry routing. Every job is quoted transparently from postcode to postcode — no callbacks, no broker chains, no surprise fees.`}
        areas={ukRegions}
      />
      <FaqSection faqs={service.faqs} />
      <RelatedServices services={siblings} heading={`Other ${SILOS[service.silo].label} Services`} />
      <RelatedLocations
        cities={featuredCities}
        serviceSlug={service.slug}
        serviceName={service.name}
        heading={`${service.name} by City`}
      />
      <CtaSection
        headline={`Get an instant ${service.name.toLowerCase()} quote`}
        subhead={`Transparent pricing in seconds. Same-day capacity in every major UK metro.`}
      />
      <SeoFooter />
    </div>
  );
}
